// ADR 30.9.26e §5 — the two raw controls the language picker needs.
//
// 8.9.26g AC3 keeps every `<button>` and `<input>` inside `ui/`, and the
// picker lives in `features/languages/`. So the trigger and the search field
// are primitives here, and the feature folder composes them.
//
// `PickerTrigger` is where `RowSelect`'s look went when the overlaid native
// `<select>` was retired. `row` is Android's tonal list row (caption, value,
// chevron). `field` is `Field`'s outlined control, from the one exported class
// string. It is a `<button aria-haspopup="dialog">`: the list is a dialog now.
//
// `SearchCombobox` is a search `<input>` that forwards its ARIA props. The
// combobox pattern needs `aria-activedescendant` / `aria-controls`, which
// `Field` has no reason to learn.

import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'

import { FOCUS_RING } from '../Button'
import { FIELD_CONTROL } from '../Field'
import { Icon } from '../icons'

const ROW =
  'relative flex w-full items-center gap-2 rounded-card bg-surface px-4 py-3 text-start ' +
  `${FOCUS_RING} disabled:opacity-70`

const FIELD = `flex items-center gap-2 text-start ${FIELD_CONTROL}`

export interface PickerTriggerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  appearance: 'row' | 'field'
  /** The caption element (`row` shows it inside; `field` above, via the caller). */
  caption: ReactNode
  /** The chosen row's label. */
  chosen: ReactNode
}

export function PickerTrigger({ appearance, caption, chosen, className = '', ...rest }: PickerTriggerProps) {
  if (appearance === 'row') {
    return (
      <button type="button" aria-haspopup="dialog" className={`${ROW} ${className}`} {...rest}>
        <span className="min-w-0 flex-1">
          {caption}
          <span className="block truncate text-body-lg text-ink">{chosen}</span>
        </span>
        <Icon name="expand_more" className="-rotate-90 text-ink-muted" />
      </button>
    )
  }
  return (
    <button type="button" aria-haspopup="dialog" className={`${FIELD} ${className}`} {...rest}>
      <span className="min-w-0 flex-1 truncate">{chosen}</span>
      <Icon name="expand_more" className="text-ink-muted" />
    </button>
  )
}

export interface SearchComboboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The listbox's id; the pattern requires both of these. */
  'aria-controls': string
  'aria-expanded': boolean
}

export const SearchCombobox = forwardRef<HTMLInputElement, SearchComboboxProps>(function SearchCombobox(
  { className = '', 'aria-controls': controls, 'aria-expanded': expanded, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type="search"
      role="combobox"
      aria-controls={controls}
      aria-expanded={expanded}
      className={`${FIELD_CONTROL} ${className}`}
      {...rest}
    />
  )
})
